"use client";

import {
	ArrowLeft,
	ArrowRight,
	ChevronLeft,
	ChevronRight,
} from "@untitledui/icons";
import { useMemo } from "react";
import {
	ButtonGroup,
	ButtonGroupItem,
} from "@/components/base/button-group/button-group";
import { Button } from "@/components/base/buttons/button";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import { cx } from "@/utils/cx";
import type { PaginationRootProps } from "./pagination-base";
import { Pagination } from "./pagination-base";
import {
	describePaginationStatus,
	getStepTarget,
	resolvePaginationModel,
} from "./pagination-model";

export {
	DEFAULT_PAGINATION_ARIA_LABEL,
	type PaginationRootProps,
} from "./pagination-base";
export {
	DEFAULT_SIBLING_COUNT,
	MAX_SIBLING_COUNT,
	type PaginationModel,
	type PaginationTotalStatus,
	resolvePaginationModel,
} from "./pagination-model";

type PaginationProps = {
	/** Whether the pagination buttons are rounded. */
	rounded?: boolean;
} & Partial<Omit<PaginationRootProps, "children">>;

const LINK_TRIGGER_TARGET = "min-h-10 min-w-10";

const PaginationItem = ({
	value,
	rounded,
	isCurrent,
}: {
	value: number;
	rounded?: boolean;
	isCurrent: boolean;
}) => (
	<Pagination.Item
		className={({ isSelected }) =>
			cx(
				"flex h-10 min-w-10 cursor-pointer items-center justify-center px-3 font-medium text-sm text-tertiary outline-focus-ring transition duration-100 ease-linear hover:bg-primary_hover hover:text-secondary focus-visible:z-10 focus-visible:bg-primary_hover focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:text-disabled disabled:hover:bg-transparent",
				rounded ? "rounded-full" : "rounded-lg",
				!!isSelected &&
					"border border-secondary_alt bg-secondary_hover text-secondary"
			)
		}
		isCurrent={isCurrent}
		value={value}
	>
		{value}
	</Pagination.Item>
);

const PaginationPageList = ({ rounded }: { rounded?: boolean }) => (
	<Pagination.Context>
		{({ pages }) =>
			pages.length > 0 ? (
				<ul className="hidden flex-wrap justify-center gap-0.5 md:flex">
					{pages.map((item) =>
						item.type === "page" ? (
							<li key={`page-${item.value}`}>
								<PaginationItem
									isCurrent={item.isCurrent}
									rounded={rounded}
									value={item.value}
								/>
							</li>
						) : (
							<li aria-hidden="true" key={`ellipsis-${item.key}`}>
								<Pagination.Ellipsis
									className="flex size-10 shrink-0 items-center justify-center text-tertiary"
									key={item.key}
								>
									&#8230;
								</Pagination.Ellipsis>
							</li>
						)
					)}
				</ul>
			) : null
		}
	</Pagination.Context>
);

const PaginationStatus = () => (
	<Pagination.Context>
		{({ pages, status }) => (
			<p
				aria-atomic="true"
				aria-live="polite"
				className={cx(
					"flex justify-center whitespace-pre text-fg-secondary text-sm",
					pages.length > 0 && "md:sr-only"
				)}
			>
				{status.kind === "page" ? (
					<>
						Página <span className="font-medium">{status.page}</span> de{" "}
						<span className="font-medium">{status.total}</span>
					</>
				) : (
					status.text
				)}
			</p>
		)}
	</Pagination.Context>
);

type MobilePaginationProps = {
	/** The current page. */
	page?: number;
	/** The total number of pages. */
	total?: number | null;
	/** The class name of the pagination component. */
	className?: string;
	/** The function to call when the page changes. */
	onPageChange?: (page: number) => void;
};

const useStepTargets = (page: unknown, total: unknown) =>
	useMemo(() => {
		const model = resolvePaginationModel({ page, total });
		return {
			prev: getStepTarget(model, "prev"),
			next: getStepTarget(model, "next"),
			status: describePaginationStatus(model),
		};
	}, [page, total]);

const StatusText = ({
	status,
}: {
	status: ReturnType<typeof describePaginationStatus>;
}) =>
	status.kind === "page" ? (
		<>
			Página <span className="font-medium">{status.page}</span> de{" "}
			<span className="font-medium">{status.total}</span>
		</>
	) : (
		status.text
	);

const MobilePagination = ({
	page = 1,
	total = 10,
	className,
	onPageChange,
}: MobilePaginationProps) => {
	const { prev, next, status } = useStepTargets(page, total);

	return (
		<nav
			aria-label="Paginação"
			className={cx("flex items-center justify-between md:hidden", className)}
		>
			<Button
				aria-label="Página anterior"
				color="secondary"
				iconLeading={ArrowLeft}
				isDisabled={prev === null}
				onClick={() => {
					if (prev !== null) {
						onPageChange?.(prev);
					}
				}}
				size="sm"
			/>

			<span className="text-fg-secondary text-sm">
				<StatusText status={status} />
			</span>

			<Button
				aria-label="Próxima página"
				color="secondary"
				iconLeading={ArrowRight}
				isDisabled={next === null}
				onClick={() => {
					if (next !== null) {
						onPageChange?.(next);
					}
				}}
				size="sm"
			/>
		</nav>
	);
};

export const PaginationPageDefault = ({
	rounded,
	page = 1,
	total = 10,
	className,
	...props
}: PaginationProps) => {
	const isDesktop = useBreakpoint("md");

	return (
		<Pagination.Root
			{...props}
			className={cx(
				"flex w-full items-center justify-between gap-3 md:pt-5",
				className
			)}
			page={page}
			total={total}
		>
			<div className="hidden flex-1 justify-start md:flex">
				<Pagination.PrevTrigger asChild>
					<Button
						className={cx(
							LINK_TRIGGER_TARGET,
							page === 1 && "cursor-not-allowed!"
						)}
						color="link-gray"
						iconLeading={ArrowLeft}
						size="sm"
					>
						{!!isDesktop && "Anterior"}
					</Button>
				</Pagination.PrevTrigger>
			</div>

			<Pagination.PrevTrigger asChild className="md:hidden">
				<Button color="secondary" iconLeading={ArrowLeft} size="sm" />
			</Pagination.PrevTrigger>

			<PaginationPageList rounded={rounded} />
			<PaginationStatus />

			<div className="hidden flex-1 justify-end md:flex">
				<Pagination.NextTrigger asChild>
					<Button
						className={cx(
							LINK_TRIGGER_TARGET,
							page === total && "cursor-not-allowed!"
						)}
						color="link-gray"
						iconTrailing={ArrowRight}
						size="sm"
					>
						{!!isDesktop && "Próxima"}
					</Button>
				</Pagination.NextTrigger>
			</div>
			<Pagination.NextTrigger asChild className="md:hidden">
				<Button color="secondary" iconTrailing={ArrowRight} size="sm" />
			</Pagination.NextTrigger>
		</Pagination.Root>
	);
};

export const PaginationPageMinimalCenter = ({
	rounded,
	page = 1,
	total = 10,
	className,
	...props
}: PaginationProps) => {
	const isDesktop = useBreakpoint("md");

	return (
		<Pagination.Root
			{...props}
			className={cx(
				"flex w-full items-center justify-between gap-3 border-secondary border-t pt-4 md:pt-5",
				className
			)}
			page={page}
			total={total}
		>
			<div className="flex flex-1 justify-start">
				<Pagination.PrevTrigger asChild>
					<Button color="secondary" iconLeading={ArrowLeft} size="sm">
						{!!isDesktop && "Anterior"}
					</Button>
				</Pagination.PrevTrigger>
			</div>

			<PaginationPageList rounded={rounded} />
			<PaginationStatus />

			<div className="flex flex-1 justify-end">
				<Pagination.NextTrigger asChild>
					<Button color="secondary" iconTrailing={ArrowRight} size="sm">
						{!!isDesktop && "Próxima"}
					</Button>
				</Pagination.NextTrigger>
			</div>
		</Pagination.Root>
	);
};

export const PaginationCardDefault = ({
	rounded,
	page = 1,
	total = 10,
	...props
}: PaginationProps) => {
	const isDesktop = useBreakpoint("md");

	return (
		<Pagination.Root
			{...props}
			className="flex w-full items-center justify-between gap-3 border-secondary border-t px-4 py-3 md:px-6 md:pt-3 md:pb-4"
			page={page}
			total={total}
		>
			<div className="flex flex-1 justify-start">
				<Pagination.PrevTrigger asChild>
					<Button color="secondary" iconLeading={ArrowLeft} size="sm">
						{!!isDesktop && "Anterior"}
					</Button>
				</Pagination.PrevTrigger>
			</div>

			<PaginationPageList rounded={rounded} />
			<PaginationStatus />

			<div className="flex flex-1 justify-end">
				<Pagination.NextTrigger asChild>
					<Button color="secondary" iconTrailing={ArrowRight} size="sm">
						{!!isDesktop && "Próxima"}
					</Button>
				</Pagination.NextTrigger>
			</div>
		</Pagination.Root>
	);
};

type PaginationCardMinimalProps = {
	/** The current page. */
	page?: number;
	/** The total number of pages. */
	total?: number | null;
	/** The alignment of the pagination. */
	align?: "left" | "center" | "right";
	/** The class name of the pagination component. */
	className?: string;
	/** The function to call when the page changes. */
	onPageChange?: (page: number) => void;
	asIcon?: boolean;
};

export const PaginationCardMinimal = ({
	page = 1,
	total = 10,
	align = "left",
	onPageChange,
	className,
	asIcon = false,
}: PaginationCardMinimalProps) => {
	const { prev, next, status } = useStepTargets(page, total);
	const prevLabel = asIcon ? "Página anterior" : undefined;
	const nextLabel = asIcon ? "Próxima página" : undefined;
	const iconSizeClass = asIcon ? "size-8" : undefined;

	return (
		<div
			className={cx(
				"border-secondary border-t px-4 py-3 md:px-6 md:pt-3 md:pb-4",
				className
			)}
		>
			<MobilePagination onPageChange={onPageChange} page={page} total={total} />

			<nav
				aria-label="Paginação"
				className={cx(
					"hidden items-center gap-3 md:flex",
					align === "center" && "justify-between"
				)}
			>
				<div className={cx(align === "center" && "flex flex-1 justify-start")}>
					<Button
						aria-label={prevLabel}
						className={cx("disabled:cursor-not-allowed!", iconSizeClass)}
						color="secondary"
						isDisabled={prev === null}
						onClick={() => {
							if (prev !== null) {
								onPageChange?.(prev);
							}
						}}
						size="sm"
					>
						{asIcon ? <ChevronLeft className="size-4" /> : "Anterior"}
					</Button>
				</div>

				<span
					className={cx(
						"font-medium text-fg-secondary text-sm",
						align === "right" && "order-first mr-auto",
						align === "left" && "order-last ml-auto"
					)}
				>
					<StatusText status={status} />
				</span>

				<div className={cx(align === "center" && "flex flex-1 justify-end")}>
					<Button
						aria-label={nextLabel}
						className={cx("disabled:cursor-not-allowed!", iconSizeClass)}
						color="secondary"
						isDisabled={next === null}
						onClick={() => {
							if (next !== null) {
								onPageChange?.(next);
							}
						}}
						size="sm"
					>
						{asIcon ? <ChevronRight className="size-4" /> : "Próxima"}
					</Button>
				</div>
			</nav>
		</div>
	);
};

interface PaginationButtonGroupProps
	extends Partial<Omit<PaginationRootProps, "children">> {
	/** The alignment of the pagination. */
	align?: "left" | "center" | "right";
}

export const PaginationButtonGroup = ({
	align = "left",
	page = 1,
	total = 10,
	...props
}: PaginationButtonGroupProps) => {
	const isDesktop = useBreakpoint("md");

	return (
		<div
			className={cx(
				"flex border-secondary border-t px-4 py-3 md:px-6 md:pt-3 md:pb-4",
				align === "left" && "justify-start",
				align === "center" && "justify-center",
				align === "right" && "justify-end"
			)}
		>
			<Pagination.Root {...props} page={page} total={total}>
				<Pagination.Context>
					{({ pages }) => (
						<ButtonGroup size="md">
							<Pagination.PrevTrigger asChild>
								<ButtonGroupItem iconLeading={ArrowLeft}>
									{!!isDesktop && "Anterior"}
								</ButtonGroupItem>
							</Pagination.PrevTrigger>

							{pages.map((item) =>
								item.type === "page" ? (
									<Pagination.Item
										asChild
										isCurrent={item.isCurrent}
										key={`page-${item.value}`}
										value={item.value}
									>
										<ButtonGroupItem
											className="h-10 min-w-10 items-center justify-center"
											isSelected={item.isCurrent}
										>
											{item.value}
										</ButtonGroupItem>
									</Pagination.Item>
								) : (
									<Pagination.Ellipsis key={item.key}>
										<ButtonGroupItem className="pointer-events-none size-10 items-center justify-center rounded-none!">
											&#8230;
										</ButtonGroupItem>
									</Pagination.Ellipsis>
								)
							)}

							<Pagination.NextTrigger asChild>
								<ButtonGroupItem iconTrailing={ArrowRight}>
									{!!isDesktop && "Próxima"}
								</ButtonGroupItem>
							</Pagination.NextTrigger>
						</ButtonGroup>
					)}
				</Pagination.Context>
			</Pagination.Root>
		</div>
	);
};
